import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys, useAuthenticatedClient } from "./queries";
import type { components } from "./schema";

/** A product we define, e.g. a Safeware warranty plan (USA-1108). Its name is `store_title`. */
export type ManualProduct = components["schemas"]["ManualProductRead"];
export type ManualProductCreate = components["schemas"]["ManualProductCreate"];
export type ManualProductUpdate = components["schemas"]["ManualProductUpdate"];

const currentManualProductsKey = ["manual-products", "current"] as const;

/** Every current product we define, for picking a warranty plan on a quote. */
export function useCurrentManualProducts(enabled = true) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: currentManualProductsKey,
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET(
        "/dashboard/manual-products",
        {},
      );
      if (error) throw error;
      return data;
    },
    enabled,
    staleTime: 60_000,
  });
}

function useInvalidateManualProducts() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: currentManualProductsKey });
    queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
  };
}

export function useCreateManualProduct(manufacturerUuid: string) {
  const getClient = useAuthenticatedClient();
  const invalidate = useInvalidateManualProducts();
  return useMutation({
    mutationFn: async (body: ManualProductCreate) => {
      const client = await getClient();
      const { data, error } = await client.POST(
        "/dashboard/manufacturers/{uuid}/manual-products",
        { params: { path: { uuid: manufacturerUuid } }, body },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateManualProduct(manufacturerUuid: string) {
  const getClient = useAuthenticatedClient();
  const invalidate = useInvalidateManualProducts();
  return useMutation({
    mutationFn: async ({
      productUuid,
      body,
    }: {
      productUuid: string;
      body: ManualProductUpdate;
    }) => {
      const client = await getClient();
      const { data, error } = await client.PATCH(
        "/dashboard/manufacturers/{uuid}/manual-products/{product_uuid}",
        {
          params: {
            path: { uuid: manufacturerUuid, product_uuid: productUuid },
          },
          body,
        },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
  });
}
